import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import { Storefront, MapPin, ArrowRight } from "@phosphor-icons/react";

const BASE = "https://thetrucktrackr.com";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About TruckTrackr",
  url: `${BASE}/about`,
  mainEntity: {
    "@type": "Organization",
    name: "TruckTrackr",
    url: BASE,
    foundingLocation: { "@type": "Place", name: "Lancaster, PA" },
    sameAs: ["https://apps.apple.com/us/app/thetrucktrackr/id6783500007"],
  },
};

export default function About() {
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16" data-testid="about-page">
      <PageMeta
        title="About TruckTrackr — Built by a Food Truck Owner"
        description="TruckTrackr was built by the owner of Ember Food Truck in Lancaster, PA to solve a real problem: telling customers where the truck will be. Here's our story."
        canonical={`${BASE}/about`}
        jsonLd={JSON_LD}
      />

      <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">Our story</span>
      <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl sm:text-5xl">
        Built by a food truck owner,
        <br />
        <span className="text-primary">not a software company.</span>
      </h1>

      <figure className="mt-8">
        <img
          src="/founder-jason-ember.jpg"
          alt="Jason Lewis, founder of TruckTrackr, standing next to his Ember food truck"
          loading="lazy"
          className="w-full max-h-[420px] object-cover object-top rounded-3xl border border-border"
          data-testid="about-founder-photo"
        />
        <figcaption className="mt-2 text-xs text-muted-foreground">
          Jason Lewis, founder of TruckTrackr, with the Ember food truck.
        </figcaption>
      </figure>

      <div className="mt-8 space-y-5 text-base text-muted-foreground leading-relaxed">
        <p>
          TruckTrackr was built by <span className="text-foreground font-semibold">Jason Lewis</span> —
          founder of TruckTrackr and owner of{" "}
          <span className="text-foreground font-semibold">Ember Food</span> in Lancaster County,
          Pennsylvania. Like every truck owner, Jason lived the same weekly grind: posting the
          schedule on Facebook, re-posting it on Instagram, answering "where are you today?"
          messages, and still having regulars miss the truck because they never saw the post.
        </p>
        <p>
          The problem wasn't marketing — it was that there was no single place where hungry
          people could just <em>look at a map</em> and see who's parked, open, and serving
          right now. So we built it.
        </p>
        <p>
          Today TruckTrackr is a live map of local food trucks. Owners post their schedule
          once and it shows up everywhere — the map, their public profile, their followers'
          notifications, even their own website. Customers open the app, see who's out, and
          go eat.
        </p>
        <p>
          Because it's built by someone who actually runs a truck, the details work the way
          trucks work: private catering days stay off the public map, schedules can repeat
          weekly, and there are no commissions or ordering fees — ever.
        </p>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        <Link to="/map" data-testid="about-cta-map">
          <Button size="lg" className="gap-2 h-12 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
            <MapPin weight="duotone" className="h-5 w-5" />
            Find trucks near me
          </Button>
        </Link>
        <Link to="/signup?role=owner" data-testid="about-cta-owner">
          <Button size="lg" variant="outline" className="gap-2 h-12 px-6 rounded-full w-full sm:w-auto">
            <Storefront weight="duotone" className="h-5 w-5" />
            Put my truck on the map
          </Button>
        </Link>
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        Questions? Email{" "}
        <a href="mailto:support@thetrucktrackr.com" className="text-primary font-semibold hover:underline">
          support@thetrucktrackr.com
        </a>{" "}
        — or see{" "}
        <Link to="/pricing" className="text-primary font-semibold hover:underline">
          plans &amp; pricing <ArrowRight className="inline h-3 w-3" />
        </Link>
      </p>
    </div>
  );
}
